import { useMemo, useState, type ReactNode } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { useResourceFiltersStore } from "@/stores/resourceFiltersStore";
import {
  ResourceMetricsChart,
  ResourceDiskIoCard,
  N8nProbePanel,
  ChangeStatusDialog,
  ChangeVmIpDialog,
  ResourceStatusBadge,
  getResourceStatusIndicator,
  VmOverviewCharts,
  VmOverviewSideColumn,
  ResourceFirewallPanel,
} from "@/components/resources";
import { APIError } from "@/lib/api";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { DeleteResourceDialog } from "@/components/resources/DeleteResourceDialog";
import { ReinstallResourceDialog } from "@/components/resources/ReinstallResourceDialog";
import { RetryProvisioningDialog } from "@/components/resources/RetryProvisioningDialog";
import { VmPasswordResetPanel } from "@/components/resources/VmPasswordResetPanel";
import { PermissionGate } from "@/components/common/PermissionGate";
import {
  BusyOverlay,
  PROVIDER_WAIT_HINT,
} from "@/components/common/BusyOverlay";
import { usePermissions } from "@/hooks/usePermissions";
import { useResourcePowerActions } from "@/hooks/useResourcePowerActions";
import {
  useOpenResourceConsole,
  useSendResourceExpiryReminder,
  useResourceStatusWatch,
  DETAIL_STATUS_WATCH_MS,
} from "@/hooks/useResourceQueries";
import { hasProviderVm, isRestorableDeletion } from "@/lib/providerVm";
import { formatVietnamDateTime } from "@/lib/date-utils";
// mayAlreadyExistOnProvider (@/lib/retryProvisioning) is superseded by the server-computed
// `retryCheck.machineMayExist` below — the helper itself is kept for reference.
import { OrderStatusBadge } from "@/components/orders/OrderStatusBadge";
import {
  ArrowLeft,
  LifeBuoy,
  SlidersHorizontal,
  Trash2,
  Copy,
  Check,
  AlertTriangle,
  BookOpen,
  Camera,
  Network,
  HardDrive,
  LayoutDashboard,
  LineChart,
  Activity as ActivityIcon,
  ScrollText,
  Code2,
  MoreHorizontal,
  Webhook,
  User,
  Clock,
  CalendarClock,
  KeyRound,
  RefreshCcwDot,
  Router,
  HardDriveDownload,
  RotateCcw,
  MailWarning,
  History,
} from "lucide-react";
import { ResourceActionsTab } from "@/components/resource-actions";
import { cn } from "@/lib/utils";
import {
  useResourceDetail,
  // useResourceActions — used only by the "Activity" card, hidden 2026-09-11; restore together.
  useResourceActivityLog,
  useResourceMetrics,
  useResourceAttachments,
  useResourceSnapshots,
  useDeleteResource,
  useResourcePowerAction,
  useReinstallResource,
  useUpdateResourceStatus,
  useUpdateResourcePublicIp,
  useApplyVmIpAddress,
  useRetryResourceProvisioning,
  useProvisioningRetryCheck,
  getProvisioningRetryCheck,
} from "@/hooks/useResourceQueries";
import { formatDate, formatDateTime } from "@/lib/utils";
import { auditActionVariant, formatAuditAction } from "@/lib/auditActions";
import { formatCurrency } from "@/lib/currency";
import {
  parseJsonArray,
  parseJsonObject,
  readLastError,
} from "@/lib/provisioning";
import type {
  // ActionStatus — used only by actionStatusVariant below; restore together.
  ResourceSnapshotStatus,
  ResourceStatus,
} from "@/types/admin";

// Used only by the "Activity" card, hidden 2026-09-11 — restore together with it.
// /**
//  * Badge styling per backend `ActionStatus`. Anything still running stays neutral — only a genuine
//  * `Failed`/`Cancelled` is shown as destructive.
//  */
// function actionStatusVariant(
//   status: ActionStatus,
// ): "default" | "secondary" | "destructive" {
//   switch (status) {
//     case "Completed":
//       return "default";
//     case "Failed":
//     case "Cancelled":
//       return "destructive";
//     default:
//       return "secondary";
//   }
// }

/**
 * Badge styling per snapshot status. Mirrors the client app's getStatusBadge
 * (ResourceSnapshotsTab.tsx) — the admin read-only list only ever sees "available" in practice
 * (the backend hardcodes that literal for every snapshot it returns), but the mapping covers the
 * full status union so a future status value doesn't silently fall through.
 */
function snapshotStatusVariant(
  status: ResourceSnapshotStatus,
): "default" | "secondary" | "destructive" | "outline" {
  switch (status) {
    case "available":
      return "default";
    case "failed":
      return "destructive";
    case "deleting":
      return "outline";
    case "creating":
    case "restoring":
    default:
      return "secondary";
  }
}

// Used only by the "Activity" card, hidden 2026-09-11 — restore together with it.
// function formatDuration(durationMs?: number | null): string {
//   if (durationMs === null || durationMs === undefined) {
//     return "—";
//   }
//
//   return durationMs < 1000
//     ? `${durationMs}ms`
//     : `${(durationMs / 1000).toFixed(1)}s`;
// }

/** Actions not yet wired to a real mutation in this pass — GET/display only for now. */
function notWiredYet() {
  toast.info("This action isn't wired up yet.");
}

/** Mirrors the client app's formatUptimeShort (VMDetailHero.tsx). */
function formatUptimeShort(start: Date, now: Date): string {
  const ms = Math.max(0, now.getTime() - start.getTime());
  const h = Math.floor(ms / 3600000);
  const d = Math.floor(h / 24);
  const hr = h % 24;
  return `${d}d ${hr}h`;
}

/** Mirrors the client app's formatMemoryMb (VMDetailHero.tsx). */
function formatMemoryMb(mb?: number): string {
  if (mb == null) return "—";
  if (mb >= 1024) return `${(mb / 1024).toFixed(mb % 1024 === 0 ? 0 : 1)} GB`;
  return `${mb} MB`;
}

/**
 * Mirrors the client app's isBootDiskDevice (ResourceStorageTab.tsx): identifies the primary
 * disk device slot across the bus types WiService uses, so the boot disk can be recognized
 * whether or not it happens to show up in the attachments list.
 */
function isBootDiskDevice(deviceId?: string | null): boolean {
  const d = (deviceId ?? "").trim().toLowerCase();
  return d === "scsi0" || d === "sata0" || d === "virtio0" || d === "ide0";
}

const tabListClass =
  "inline-flex h-auto min-h-9 flex-wrap gap-0.5 rounded-[10px] border border-border/80 bg-muted/40 p-1 text-muted-foreground";
const tabTriggerClass =
  "cursor-pointer gap-2 rounded-md px-3 py-2 text-sm font-medium data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow-sm";

const METRICS_TIME_RANGES = [
  { value: "1h", label: "1H", windowMs: 60 * 60 * 1000, interval: "1m" },
  {
    value: "24h",
    label: "24H",
    windowMs: 24 * 60 * 60 * 1000,
    interval: "15m",
  },
  {
    value: "7d",
    label: "7D",
    windowMs: 7 * 24 * 60 * 60 * 1000,
    interval: "1h",
  },
  {
    value: "30d",
    label: "30D",
    windowMs: 30 * 24 * 60 * 60 * 1000,
    interval: "1d",
  },
  {
    value: "1y",
    label: "1Y",
    windowMs: 365 * 24 * 60 * 60 * 1000,
    interval: "1d",
  },
] as const;

type MetricsTimeRange = (typeof METRICS_TIME_RANGES)[number]["value"];

// Mirrors the client app's lib/resourceStatus.ts: metrics only exist once the VM is on the
// hypervisor. Extended with an `isVm` check on the call site since (unlike the client, which
// only ever renders this for VM resources) the admin Metrics tab is reachable for any resource
// type, and the backend only ever populates real data for VirtualMachine.
const METRICS_READY_STATUSES: ResourceStatus[] = [
  "Running",
  "Stopped",
  "Suspended",
];
const METRICS_PENDING_STATUSES: ResourceStatus[] = ["Provisioning"];

/**
 * Config change (the paid resize) is off for now.
 *
 * Temporary: the flow resizes the customer's VM and records a payment against their order, and it
 * is not ready to be run by hand. Flip this back to `true` to restore the menu item — the command,
 * the page, its route (App.tsx registers it only while this is on) and the endpoint are all still wired, nothing else needs changing.
 */
export const CONFIG_CHANGE_ENABLED: boolean = false;

/**
 * A tab whose own query failed, with the way to ask again.
 *
 * Every tab used to stop at the alert, so one failed request — a provider blip on attachments or
 * snapshots, a token refresh losing a race — left the operator reloading the whole page to see a
 * tab that would most likely answer on the second try. Retry sits in the alert, next to the
 * reason; a 404 gets none, because asking again for a resource that is gone only fails again.
 *
 * `notReadyAware` keeps the amber "Not available yet" treatment to the provider-backed tabs that
 * answer 409 while a VM is still being built; the database-only tabs never do.
 *
 * `stale` is the case where the tab already has rows and only a refetch failed — TanStack keeps
 * `data` and sets `error` together. Every write on this page now waits for its refetch, so that case
 * is common; replacing rows the operator can still read with a red alert would throw them away. The
 * caller renders this above the rows instead, as a banner.
 */
function TabQueryError({
  error,
  failedTitle,
  onRetry,
  isRetrying,
  notReadyAware = false,
  stale = false,
}: {
  error: Error;
  failedTitle: string;
  onRetry: () => void;
  isRetrying: boolean;
  notReadyAware?: boolean;
  stale?: boolean;
}) {
  const status = error instanceof APIError ? error.statusCode : undefined;

  if (stale && status !== 404) {
    return (
      <Alert variant="default" className="mb-4 border-warn/25 bg-warn-soft">
        <AlertTriangle className="h-4 w-4 text-warn" />
        <AlertTitle className="text-warn">
          Could not refresh — showing the last loaded data
        </AlertTitle>
        <AlertDescription className="text-warn">
          <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
            <span>{error.message || "An unexpected error occurred."}</span>
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={onRetry}
              disabled={isRetrying}
            >
              {isRetrying && <Spinner className="mr-2" />}
              Retry
            </Button>
          </div>
        </AlertDescription>
      </Alert>
    );
  }

  const retryButton = (
    <div className="mt-3">
      <Button
        type="button"
        size="sm"
        variant="outline"
        onClick={onRetry}
        disabled={isRetrying}
      >
        {isRetrying && <Spinner className="mr-2" />}
        Retry
      </Button>
    </div>
  );

  if (notReadyAware && status === 409) {
    return (
      <Alert variant="default" className="border-warn/25 bg-warn-soft">
        <AlertTriangle className="h-4 w-4 text-warn" />
        <AlertTitle className="text-warn">Not available yet</AlertTitle>
        <AlertDescription className="text-warn">
          {error.message}
          {retryButton}
        </AlertDescription>
      </Alert>
    );
  }

  if (status === 404) {
    return (
      <Alert variant="destructive">
        <AlertTriangle className="h-4 w-4" />
        <AlertTitle>Resource not found</AlertTitle>
        <AlertDescription>{error.message}</AlertDescription>
      </Alert>
    );
  }

  return (
    <Alert variant="destructive">
      <AlertTriangle className="h-4 w-4" />
      <AlertTitle>{failedTitle}</AlertTitle>
      <AlertDescription>
        {error.message || "An unexpected error occurred."}
        {retryButton}
      </AlertDescription>
    </Alert>
  );
}

export function ResourceDetailPage() {
  const [isChangingStatus, setIsChangingStatus] = useState(false);
  const [isChangingIp, setIsChangingIp] = useState(false);
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("overview");
  const [metricsTimeRange, setMetricsTimeRange] =
    useState<MetricsTimeRange>("1h");
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  // The in-place reinstall on this (live) VM — one dialog, one request (design doc §5a). Its own
  // flag rather than a mode on the delete dialog: reinstalling is not a deletion any more.
  const [confirmReinstallOpen, setConfirmReinstallOpen] = useState(false);
  const [confirmRetryOpen, setConfirmRetryOpen] = useState(false);
  // Controlled so a power action fired from inside it can hold it open until the request settles.
  const [actionsMenuOpen, setActionsMenuOpen] = useState(false);
  const [copiedField, setCopiedField] = useState<string | null>(null);
  const deleteResource = useDeleteResource();
  // The delete dialog's own power steps (shut down / resume); SuperAdmin only, like the menu's.
  const deleteDialogPowerAction = useResourcePowerAction();
  const reinstallResource = useReinstallResource();
  // Where the page lands after a restorable deletion: the list's default "Active" view hides deleted
  // rows, so it opens on Status = Decommissioning, where the row now is. (Until 2026-10-14 this set
  // the retired "Pending purge (≤1h)" deletion filter.)
  const setListFilter = useResourceFiltersStore((state) => state.setFilter);
  const updateStatus = useUpdateResourceStatus();
  const updatePublicIp = useUpdateResourcePublicIp();
  const applyVmIp = useApplyVmIpAddress();
  const retryProvisioning = useRetryResourceProvisioning();
  const { isSuperAdmin } = usePermissions();

  const {
    data: resource,
    isLoading,
    isFetching,
    error,
    refetch,
  } = useResourceDetail(id!);
  const isVm = resource?.type === "VirtualMachine";
  // The admin detail query applies the soft-delete filter, so a row that loads at all and reads
  // Decommissioning can only be a live reinstall in flight — never a deleted one (design doc §7).
  const isReinstallingLive = resource?.status === "Decommissioning";

  // Server-decided (2026-09-28): checked only for a VM currently in Error — every other
  // status/type is refused the same way the mutation itself would refuse it. Open to every admin
  // role; only a SuperAdmin may actually press Retry (see the menu item below).
  const retryCheckIds =
    isVm && resource && resource.status === "Error" ? [resource.id] : [];
  const { data: retryChecks } = useProvisioningRetryCheck(retryCheckIds);
  const retryCheck = getProvisioningRetryCheck(retryChecks, resource?.id);

  // While Terminating, only the status is re-read. Once n8n confirms, the page has nothing left to
  // show (the hook drops the detail and toasts), so it returns to the list; a refusal refetches the
  // detail, which shows the banner below. A live reinstall is watched the same way (§14 3.6), ending
  // on Running/Stopped/Error rather than leaving the page.
  const watchedIds =
    resource && (resource.status === "Terminating" || isReinstallingLive)
      ? [resource.id]
      : [];
  const reinstallingWatchIds =
    resource && isReinstallingLive ? [resource.id] : [];
  const {
    isWatchCapExpired: isReinstallWatchCapExpired,
    resumeWatch: resumeReinstallWatch,
  } = useResourceStatusWatch(
    watchedIds,
    DETAIL_STATUS_WATCH_MS,
    () => {
      setListFilter("status", "Decommissioning");
      navigate("/admin/resources");
    },
    reinstallingWatchIds,
  );

  // Memoised on the three fields the dialog reads, not built inline at the call site:
  // ChangeStatusDialog re-seeds its select whenever this prop's identity changes, so a fresh
  // object literal every render would snap the operator's choice back to the current status
  // the moment they picked anything else.
  const statusTarget = useMemo(
    () =>
      isChangingStatus && resource
        ? {
            name: resource.name,
            type: resource.type,
            status: resource.status,
          }
        : null,
    // Deliberately the three fields rather than `resource` itself: the detail query refetches,
    // and any other field changing under an open dialog would reset a selection the operator has
    // already made.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isChangingStatus, resource?.name, resource?.type, resource?.status],
  );

  // Memoised on the fields the dialog reads, for the same reason as `statusTarget` above:
  // ChangeVmIpDialog re-seeds its input whenever this prop's identity changes, so a fresh object
  // literal every render would snap the operator's typing back to the address on record.
  const changeIpTarget = useMemo(
    () =>
      isChangingIp && resource
        ? {
            name: resource.name,
            status: resource.status,
            publicIpAddress: resource.vm?.publicIpAddress ?? null,
            providerResourceId: resource.providerResourceId,
          }
        : null,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      isChangingIp,
      resource?.name,
      resource?.status,
      resource?.vm?.publicIpAddress,
      resource?.providerResourceId,
    ],
  );

  // Same seven actions and confirmations as the resource table's row menu — and, like that menu,
  // all seven live together in the overflow rather than being split across two surfaces.
  const {
    actions: powerActions,
    confirmDialog: powerConfirmDialog,
    isPending: isRunningPowerAction,
    blockedReason: powerBlockedReason,
  } = useResourcePowerActions(resource);
  const sendReminder = useSendResourceExpiryReminder();
  const vmConsole = useOpenResourceConsole(id!);
  const metricsReady =
    isVm &&
    !!resource?.status &&
    METRICS_READY_STATUSES.includes(resource.status);
  const metricsPending =
    isVm &&
    !!resource?.status &&
    METRICS_PENDING_STATUSES.includes(resource.status);
  // Link state for the Network tab. Mirrors the client's `publicInterfaceStatus`: nothing on the
  // wire reports per-NIC state, so a Running VM is the only thing that counts as "up".
  const isInterfaceUp = resource?.status === "Running";

  // Read only by the "Activity" card, hidden 2026-09-11 — the "Resource Actions" tab reads the same
  // rows with paging. Commented out with it so the page does not fetch data it no longer shows.
  // const {
  //   data: actions,
  //   isLoading: isLoadingActions,
  //   isFetching: isFetchingActions,
  //   error: actionsError,
  //   refetch: refetchActions,
  // } = useResourceActions(id!);
  const {
    data: activityLog,
    isLoading: isLoadingActivityLog,
    isFetching: isFetchingActivityLog,
    error: activityLogError,
    refetch: refetchActivityLog,
  } = useResourceActivityLog(id!);
  const {
    data: attachments,
    isLoading: isLoadingAttachments,
    isFetching: isFetchingAttachments,
    error: attachmentsError,
    refetch: refetchAttachments,
  } = useResourceAttachments(id!, isVm);
  // Gated on the tab, like the two metrics queries below, and for a stronger reason: this is the
  // only query on the page that leaves the database. GetResourceSnapshotsQueryHandler calls
  // IWiServiceClient, so every visit to this page - Overview included - used to spend a provider
  // round trip on a list nobody had asked to see. The Overview's Backup card already has copy for
  // the not-yet-known case ("Configure recurring snapshots in the Snapshots tab"), so nothing on
  // screen is lost by waiting until the operator opens the tab.
  const {
    data: snapshots,
    isLoading: isLoadingSnapshots,
    isFetching: isFetchingSnapshots,
    error: snapshotsError,
    refetch: refetchSnapshots,
  } = useResourceSnapshots(id!, isVm && activeTab === "snapshots");

  const selectedRange = METRICS_TIME_RANGES.find(
    (r) => r.value === metricsTimeRange,
  )!;
  const now = new Date();

  const {
    data: metrics,
    isLoading: isLoadingMetrics,
    isFetching: isFetchingMetrics,
    isPlaceholderData: isSwitchingMetricsRange,
    error: metricsError,
    refetch: refetchMetrics,
  } = useResourceMetrics(
    id!,
    {
      windowMs: selectedRange.windowMs,
      interval: selectedRange.interval,
      rangeKey: selectedRange.value,
    },
    // Gated on the tab as well as the status. Both metrics queries are declared here at page
    // level, so neither unmounts with its tab, and `useResourceMetrics` refetches once a minute
    // — without this the page made two provider-backed metrics calls every minute no matter
    // which tab the operator was actually looking at.
    //
    // The Storage tab's Disk I/O card reads this same series, so it counts as the tab too. Gated on
    // Metrics alone, Storage showed "no throughput" for a query that had never run — or a stale
    // series that never polled.
    metricsReady && (activeTab === "metrics" || activeTab === "storage"),
  );

  // The Overview tab's mini charts always show the last hour, independent of whatever
  // range is selected on the Metrics tab. Same key as the Metrics tab's 1H range — the request is
  // identical — so moving between the two tabs reads one cached series instead of fetching twice.
  //
  // `metricsReady`, not `isVm`: metrics only exist once the VM is on the hypervisor (see the
  // constants above), and this query polls once a minute. Gated on `isVm` alone it kept asking
  // for metrics of a VM that was still provisioning, or had errored, or was already terminated —
  // forever, once a minute. The Metrics tab query has always had this gate.
  const {
    data: overviewMetrics,
    isLoading: isLoadingOverviewMetrics,
    error: overviewMetricsError,
  } = useResourceMetrics(
    id!,
    { windowMs: 60 * 60 * 1000, interval: "1m", rangeKey: "1h" },
    metricsReady && activeTab === "overview",
  );

  // `specifications` and `tags` arrive as JSON strings, not objects — see ResourceDetail.
  const specifications = parseJsonObject(resource?.specifications);
  const tags = parseJsonArray(resource?.tags);

  // Only while the resource is at Error: a retry clears the line, but a resource that recovered
  // some other way can keep the one from the failure it survived.
  const lastError =
    resource?.status === "Error" ? readLastError(resource.metadata) : null;

  // Boot-disk synthesis for the Storage tab (mirrors ResourceStorageTab.tsx on the client): the
  // boot disk is part of the VM's plan, not a row `/attachments` ever returns unless it was also
  // separately attached — so when it's missing from `attachments.disks`, synthesize one row from
  // the plan specs the VM was created with. Without this, a VM that only has its boot disk shows
  // an empty disk list even though it plainly has storage.
  const attachedDisks = attachments?.disks ?? [];
  const hasBootDiskAttachment = attachedDisks.some(
    (d) => isBootDiskDevice(d.deviceId) || !d.deviceId,
  );
  const bootDiskGb =
    typeof specifications.disk === "number" ? specifications.disk : undefined;
  const bootStorageTier =
    typeof specifications.storageTier === "string"
      ? specifications.storageTier
      : undefined;

  interface StorageVolumeRow {
    key: string;
    name: string;
    deviceId?: string;
    sizeGB?: number;
    iops?: number;
    attachedAt?: string;
    isBoot: boolean;
    /** Where the volume lands in the guest — only the boot disk has a known mount point. */
    mountLabel: string;
    /** Plan-provided (boot, showing its storage tier) vs separately attached. */
    typeLabel: string;
  }

  const storageVolumes: StorageVolumeRow[] = [];
  if (!hasBootDiskAttachment && bootDiskGb != null) {
    storageVolumes.push({
      key: "plan-boot-disk",
      name: "Boot disk",
      deviceId: "scsi0",
      sizeGB: bootDiskGb,
      isBoot: true,
      mountLabel: "/ (OS)",
      typeLabel: bootStorageTier ?? "Plan",
    });
  }
  for (const disk of attachedDisks) {
    const isBoot = isBootDiskDevice(disk.deviceId) || !disk.deviceId;

    storageVolumes.push({
      key: disk.attachmentId,
      name: disk.resourceName,
      deviceId: disk.deviceId,
      sizeGB: disk.sizeGB,
      iops: disk.iops,
      attachedAt: disk.attachedAt,
      isBoot,
      mountLabel: isBoot ? "/ (OS)" : "—",
      typeLabel: isBoot ? (bootStorageTier ?? "Plan") : "Attached",
    });
  }

  const handleCopy = async (field: string, value: string) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopiedField(field);
      setTimeout(() => setCopiedField(null), 2000);
    } catch {
      toast.error("Failed to copy to clipboard");
    }
  };

  // The delete endpoint answers 409 for a resource already on its way out, for one still
  // provisioning — deleting mid-provision races ProvisionResourceJob and can orphan a VM — and for
  // one being reinstalled (RESOURCE_REINSTALLING). Mirrors `ResourceActionsDropdown`'s
  // `isNotDeletable`.
  const isNotDeletable =
    resource?.status === "Terminating" ||
    resource?.status === "Terminated" ||
    resource?.status === "Provisioning" ||
    resource?.status === "Decommissioning";

  const deleteBlockedReason =
    resource?.status === "Provisioning"
      ? "Still provisioning — wait for Running or Error"
      : resource?.status === "Decommissioning"
        ? "Being reinstalled"
        : "Already deleted";

  // The item stays in the menu on every other status — the dialog's own gate (design doc §5a rows
  // 1/3/4) is where "shut it down first", "no machine to rebuild" and "the term has ended" are
  // explained — but a row already on its way out, or already reinstalling, has nothing to offer the
  // dialog. Mirrors `ResourceActionsDropdown`'s `canReinstallLive`, which also hides it on Error: that
  // VM cannot be stopped, and a failed one is retried or recovered, never reinstalled.
  const canOfferReinstall = resource?.status !== "Error";
  const isNotReinstallable =
    resource?.status === "Terminating" ||
    resource?.status === "Terminated" ||
    resource?.status === "Provisioning" ||
    resource?.status === "Decommissioning";

  const reinstallBlockedReason =
    resource?.status === "Provisioning"
      ? "Still provisioning — wait for Running or Error"
      : resource?.status === "Decommissioning"
        ? "Already reinstalling"
        : "Already deleted";

  // The backend refuses "Update resource by UUID" on a reinstalling VM (RESOURCE_REINSTALLING):
  // a live reinstall is its own round trip to the provider, and running this one at the same time
  // would race it. The item stays visible on Decommissioning, unlike Terminated/Terminating below,
  // because it is transient — the operator sees why rather than the item just vanishing.
  const updateBlockedReason = isReinstallingLive
    ? "Being reinstalled"
    : undefined;

  // Expiry only means something for a VM — the expiry scan jobs act on virtual machines alone —
  // and the endpoint rejects a resource that is on its way out. Offered here as well as in the
  // table because an expired VM has no power actions left: extending it is the only way forward,
  // and sending the operator back to the list to find it is a dead end on this page.
  const canEditExpiry =
    isVm &&
    resource?.status !== "Terminating" &&
    resource?.status !== "Terminated";

  // While a power action or the reminder is in flight, the menu's other writes are held and the menu
  // trigger shows the spinner. The menu itself closes on select: a Radix menu is modal, so holding it
  // open for a provider round trip locked the whole console with nothing on screen saying why.
  const isMenuBusy = isRunningPowerAction || sendReminder.isPending;

  // The record-level writes cover the page instead: each one is started from a dialog, and the page
  // behind it — the power items, the other dialogs, the tabs — must not take a second write on the
  // same VM until the re-read shows what the first one did.
  const busyLabel = deleteResource.isPending
    ? "Deleting resource…"
    : reinstallResource.isPending
      ? "Starting reinstall…"
      : updateStatus.isPending
        ? "Updating status…"
        : applyVmIp.isPending
          ? "Applying the IP address to the machine…"
          : updatePublicIp.isPending
            ? "Updating IP address…"
            : retryProvisioning.isPending
              ? "Retrying provisioning…"
              : null;

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate("/admin/resources")}
          className="mb-4"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Resources
        </Button>
        {/* Skeleton in the shape of the page, not a line of text in an empty card. The detail
            query is DB-only and quick, but the four that follow it (actions, activity,
            attachments and the provider-backed snapshots) land at different times, so a page
            that appears from nothing reads as a stall and then a jolt. Blocking out the hero,
            the stat row and the tab body keeps the layout still while they arrive. */}
        <Card className="border-border/80 shadow-none">
          <CardContent className="space-y-4 p-4">
            <div className="flex items-center gap-4">
              <Skeleton className="h-12 w-12 rounded-full" />
              <div className="space-y-2">
                <Skeleton className="h-5 w-56" />
                <Skeleton className="h-4 w-72" />
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {Array.from({ length: 4 }, (_, index) => (
            <Card key={index} className="border-border/80 shadow-none">
              <CardContent className="space-y-2 p-4">
                <Skeleton className="h-4 w-24" />
                <Skeleton className="h-6 w-16" />
              </CardContent>
            </Card>
          ))}
        </div>

        <Card className="border-border/80 shadow-none">
          <CardContent className="space-y-3 p-4">
            <Skeleton className="h-9 w-full max-w-lg" />
            <Skeleton className="h-24 w-full" />
            <Skeleton className="h-24 w-full" />
          </CardContent>
        </Card>
      </div>
    );
  }

  // Only when there is nothing to show. TanStack keeps `data` when a refetch fails, and every write
  // on this page now waits for one — so `error || !resource` threw a page the operator could still
  // read away for a blip on the re-read that follows a power action. With the resource in hand the
  // failure is a banner under the hero instead (below).
  if (!resource) {
    return (
      <div className="space-y-6">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate("/admin/resources")}
          className="mb-4"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Resources
        </Button>
        <Card className="border-border/80 shadow-none">
          <CardContent className="py-12 text-center">
            <p className="text-destructive-ink">Failed to load resource</p>
            {/* Retry first, and only then the way out. A single failed request - a token refresh
                losing a race, a blip on reload - used to leave "Return to Resources" as the only
                move, so the operator had to walk back to the list and find the row again to ask
                for the very same page. */}
            <div className="mt-4 flex items-center justify-center gap-2">
              <Button onClick={() => refetch()} disabled={isFetching}>
                {isFetching && <Spinner className="mr-2" />}
                Retry
              </Button>
              <Button
                onClick={() => navigate("/admin/resources")}
                variant="outline"
              >
                Return to Resources
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const statusIndicator = getResourceStatusIndicator(resource.status);
  const avatarInitial = resource.name.slice(0, 1).toUpperCase() || "R";
  const specsLabel =
    resource.vm?.vcpu != null
      ? `${resource.vm.vcpu} vCPU`
      : typeof specifications.cores === "number"
        ? `${specifications.cores} vCPU`
        : "—";
  const memoryLabel = formatMemoryMb(
    resource.vm?.memoryMb ??
      (typeof specifications.memory === "number"
        ? specifications.memory
        : undefined),
  );

  // Mirrors the client app's VMDetailHero stat strip exactly (Shape / Public IP / Uptime /
  // Monthly / Valid until / Backup) — region and tenant are shown elsewhere on this page
  // (System Information card), since client doesn't need either for its own tenant's VM.
  const launchedDate = new Date(resource.provisionedAt || resource.createdAt);
  const uptimeLabel = formatUptimeShort(launchedDate, now);

  const hourly =
    resource.hourlyCost > 0
      ? resource.hourlyCost
      : resource.monthlyPrice > 0
        ? Math.round((resource.monthlyPrice / 720) * 1000) / 1000
        : 0;

  const validUntilDate = resource.expiresAt
    ? new Date(resource.expiresAt)
    : null;
  const validUntilOk =
    validUntilDate && !Number.isNaN(validUntilDate.getTime());
  const daysLeft = validUntilOk
    ? Math.ceil(
        (validUntilDate!.getTime() - now.getTime()) / (1000 * 60 * 60 * 24),
      )
    : null;
  const validUntilTone =
    daysLeft === null
      ? ""
      : daysLeft <= 1
        ? "text-destructive"
        : daysLeft <= 7
          ? "text-warn"
          : "";

  // Undefined until the Snapshots tab has been opened at least once - the query behind it is
  // gated on that tab. The Backup card reads the null case as guidance rather than a count.
  const snapshotCount = isVm ? snapshots?.length : undefined;

  const heroStats: { label: string; main: ReactNode; sub: string }[] = [
    {
      label: "Shape",
      main: specsLabel,
      sub: memoryLabel || "—",
    },
    {
      label: "Public IP",
      main: resource.vm?.publicIpAddress ? (
        <span className="flex items-center gap-1 font-mono">
          {resource.vm.publicIpAddress}
          <button
            type="button"
            className="cursor-pointer text-muted-foreground transition-colors hover:text-foreground"
            onClick={() => handleCopy("heroIp", resource.vm!.publicIpAddress!)}
            aria-label="Copy IP address"
          >
            {copiedField === "heroIp" ? (
              <Check className="h-3 w-3" />
            ) : (
              <Copy className="h-3 w-3" />
            )}
          </button>
        </span>
      ) : (
        "—"
      ),
      sub: "Primary IPv4",
    },
    {
      label: "Uptime",
      main: uptimeLabel,
      sub: `Created ${formatDateTime(launchedDate.toISOString())}`,
    },
    {
      label: "Monthly Price",
      main: (
        <span className="tabular-nums">
          {formatCurrency(resource.monthlyPrice, resource.currency)}
        </span>
      ),
      sub: `≈ ${formatCurrency(hourly, resource.currency)}/hour`,
    },
    {
      label: "Valid Until",
      main: validUntilOk ? (
        <span className={cn("tabular-nums", validUntilTone)}>
          {formatDateTime(validUntilDate!.toISOString())}
        </span>
      ) : (
        "—"
      ),
      sub:
        daysLeft !== null && daysLeft <= 7
          ? daysLeft <= 0
            ? "Expired"
            : `Expires in ${daysLeft}d`
          : "Prepaid cycle",
    },
    {
      label: "Backup",
      main: "Snapshot",
      sub:
        snapshotCount != null
          ? `${snapshotCount} snapshot${snapshotCount === 1 ? "" : "s"}`
          : "Configure recurring snapshots in the Snapshots tab.",
    },
  ];

  return (
    <div className="space-y-5 pb-10">
      <div
        className={cn(
          "rounded-2xl border border-border/80 bg-card p-5 sm:p-6",
          "bg-[radial-gradient(800px_240px_at_100%_-10%,hsl(var(--primary)/0.08),transparent_60%),radial-gradient(600px_200px_at_0%_-20%,hsl(var(--brand)/0.06),transparent_60%)]",
        )}
      >
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between lg:gap-6">
          <div className="flex min-w-0 items-start gap-4">
            <div className="relative shrink-0">
              <div
                className={cn(
                  "flex h-16 w-16 items-center justify-center rounded-2xl text-2xl font-semibold text-primary-foreground shadow-inner",
                  "bg-gradient-to-br from-primary to-[hsl(var(--brand-hover))] ring-2 ring-offset-2 ring-offset-card",
                  statusIndicator.ringClassName,
                )}
              >
                {avatarInitial}
              </div>
              <span
                className={cn(
                  "absolute -bottom-0.5 -right-0.5 h-4 w-4 rounded-full border-2 border-card",
                  statusIndicator.dotClassName,
                )}
                aria-hidden
              />
            </div>
            <div className="min-w-0 flex-1">
              <h1 className="text-xl font-semibold tracking-tight md:text-2xl break-all">
                {resource.name}
              </h1>
              <div className="mt-2 flex flex-wrap gap-1.5">
                <span className="rounded-full border border-brand/25 bg-brand-soft px-2.5 py-0.5 text-[11px] font-medium text-brand">
                  {resource.vm?.operatingSystem || resource.type}
                </span>
                <ResourceStatusBadge
                  status={resource.status}
                  className="min-h-0 px-2.5 py-0.5 text-[11px]"
                />
                {resource.region && (
                  <span className="rounded-full border border-border bg-muted/50 px-2.5 py-0.5 text-[11px] text-muted-foreground">
                    {resource.region}
                  </span>
                )}
              </div>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-1.5 shrink-0">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate("/admin/resources")}
              className="h-8 cursor-pointer"
            >
              <ArrowLeft className="mr-2 h-3.5 w-3.5" />
              Back to Resources
            </Button>
            {/* The client hero's Console button. SuperAdmin-only, like the endpoint. No status or
                expiry gate, unlike the client: the admin endpoint has none, and an operator looking
                into a stopped or lapsed VM is who needs the screen. Only a row with no machine
                behind it is refused, because no request could succeed. */}
            {isVm && (
              <PermissionGate roles="SuperAdmin">
                <Button
                  variant="outline"
                  size="sm"
                  className="h-8 gap-1.5"
                  disabled={
                    vmConsole.isPending ||
                    !hasProviderVm(resource.providerResourceId)
                  }
                  title={
                    hasProviderVm(resource.providerResourceId)
                      ? "Open the web console (noVNC)"
                      : "No VM on record yet"
                  }
                  onClick={vmConsole.openConsole}
                >
                  {vmConsole.isPending ? (
                    <Spinner />
                  ) : (
                    <Code2 className="h-3.5 w-3.5" />
                  )}
                  {vmConsole.isPending ? "Opening…" : "Console"}
                </Button>
              </PermissionGate>
            )}
            <DropdownMenu
              open={actionsMenuOpen}
              onOpenChange={setActionsMenuOpen}
            >
              <DropdownMenuTrigger asChild>
                {/* Icon-only, so the label is the button's only name: without it a screen reader
                    announces a bare "button", and nothing can find the menu by role. It spins while a
                    power action or the reminder is in flight — the menu has already closed. */}
                <Button
                  size="sm"
                  className="h-8 cursor-pointer gap-1 px-2.5"
                  aria-label="More actions"
                  aria-busy={isMenuBusy}
                  title="More actions"
                >
                  {isMenuBusy ? (
                    <Spinner />
                  ) : (
                    <MoreHorizontal className="h-4 w-4" />
                  )}
                </Button>
              </DropdownMenuTrigger>
              {/* Three tiers, and the split is what each one acts on rather than how dangerous it
                  feels: "Inspect" only opens a view, "Virtual machine" reaches the hypervisor, and
                  "WiCloud record" changes nothing but WiCloud's own data. Delete is the only item
                  that does both, which is why it sits alone at the bottom. */}
              <DropdownMenuContent align="end" className="w-72">
                <DropdownMenuLabel className="text-xs font-medium text-muted-foreground">
                  Inspect
                </DropdownMenuLabel>
                {isVm && (
                  <DropdownMenuItem
                    className="cursor-pointer"
                    onClick={() => setActiveTab("snapshots")}
                  >
                    <Camera className="mr-2 h-4 w-4" />
                    Snapshots
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem
                  className="cursor-pointer"
                  onClick={() => setActiveTab("metrics")}
                >
                  <LineChart className="mr-2 h-4 w-4" />
                  Metrics
                </DropdownMenuItem>
                {/* Same link as the row menu: the guide opens in its own tab, at "which tool". */}
                <DropdownMenuItem asChild className="cursor-pointer">
                  <a
                    href="/docs/vm-actions.html#which-tool"
                    target="_blank"
                    rel="noreferrer"
                  >
                    <BookOpen className="mr-2 h-4 w-4" />
                    How these actions work
                  </a>
                </DropdownMenuItem>

                {/* Its own group: the only item in this menu that leaves WiCloud and reaches the
                    customer, while touching neither the hypervisor nor the record. Offered to
                    SupportAdmin too — they are who takes the call when a customer says the
                    reminder never arrived. Kept in step with the row menu on the list, which
                    carries the same item in the same place. */}
                {isVm && resource && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuLabel className="text-xs font-medium text-muted-foreground">
                      Customer
                    </DropdownMenuLabel>
                    <DropdownMenuItem
                      className="cursor-pointer"
                      disabled={isMenuBusy}
                      // Closes on select like every other item: the trigger spins until the request
                      // answers, and the toast reports the outcome.
                      onClick={() => sendReminder.mutate(resource.id)}
                    >
                      {sendReminder.isPending ? (
                        <Spinner className="mr-2" />
                      ) : (
                        <MailWarning className="mr-2 h-4 w-4" />
                      )}
                      Send expiry reminder
                    </DropdownMenuItem>
                  </>
                )}

                {/* SuperAdmin-only, mirroring the [AdminRole] gate on the power, expiry and delete
                    endpoints. */}
                <PermissionGate roles="SuperAdmin">
                  {/* All seven, always, for any status these two grouped statuses don't block —
                      the only items here that reach the hypervisor. The admin endpoint refuses
                      Terminating/Decommissioning with INVALID_STATE (both are already their own
                      round trip to the provider), so those two disable the group instead of
                      leaving it to come back as a toast. Anything else the VM cannot take is
                      refused by the provider and reported the same way. */}
                  {powerActions.length > 0 && (
                    <>
                      <DropdownMenuSeparator />
                      <DropdownMenuLabel className="text-xs font-medium text-muted-foreground">
                        Virtual machine
                      </DropdownMenuLabel>
                    </>
                  )}
                  {powerActions.map(
                    ({ action, label, Icon, destructive, isRunning, run }) =>
                      destructive ? (
                        // Opens a confirmation instead of firing, so the menu can close right
                        // away — the dialog is where this action reports back.
                        <DropdownMenuItem
                          key={action}
                          className="cursor-pointer text-destructive focus:text-destructive"
                          disabled={isMenuBusy || powerBlockedReason !== null}
                          title={powerBlockedReason ?? undefined}
                          onClick={() => run()}
                        >
                          <Icon className="mr-2 h-4 w-4" />
                          {label}
                        </DropdownMenuItem>
                      ) : (
                        <DropdownMenuItem
                          key={action}
                          className="cursor-pointer"
                          disabled={isMenuBusy || powerBlockedReason !== null}
                          title={powerBlockedReason ?? undefined}
                          // Closes on select: the trigger spins and the menu's other writes stay
                          // held until the request settles, and the toast reports the outcome.
                          onClick={() => run()}
                        >
                          {isRunning ? (
                            <Spinner className="mr-2" />
                          ) : (
                            <Icon className="mr-2 h-4 w-4" />
                          )}
                          {label}
                        </DropdownMenuItem>
                      ),
                  )}
                </PermissionGate>

                {isVm && (
                  // One entry point for both layers; the dialog is where the operator chooses
                  // whether the change reaches the machine or only WiCloud's record. Grouped with
                  // the actions that touch the hypervisor because the group has to describe the
                  // worst this item can do.
                  <PermissionGate roles="SuperAdmin">
                    <DropdownMenuItem
                      className="cursor-pointer"
                      disabled={isMenuBusy}
                      onClick={() => setIsChangingIp(true)}
                    >
                      <Router className="mr-2 h-4 w-4" />
                      Change IP address
                    </DropdownMenuItem>

                    {/* The in-place reinstall (design doc §5a) — one request, no wipe step. Kept
                        in this group because it reaches the hypervisor and costs more than
                        anything else here: the disk is erased. */}
                    {canOfferReinstall && (
                      <DropdownMenuItem
                        className="cursor-pointer text-destructive focus:text-destructive"
                        disabled={
                          reinstallResource.isPending ||
                          isNotReinstallable ||
                          isMenuBusy
                        }
                        title={
                          isNotReinstallable
                            ? reinstallBlockedReason
                            : undefined
                        }
                        onClick={() => setConfirmReinstallOpen(true)}
                      >
                        <HardDriveDownload className="mr-2 h-4 w-4" />
                        Reinstall…
                      </DropdownMenuItem>
                    )}
                  </PermissionGate>
                )}

                {/* Server-decided (2026-09-28): `retryCheck` (the batch check, open to every admin
                    role) already reflects the status, failure kind, order, billing-cycle and 24h
                    window, so the item is shown whenever it says allowed — outside the SuperAdmin
                    gate above, so a BillingAdmin/SupportAdmin sees it too, disabled with a reason.
                    Only a SuperAdmin may actually press it; the POST stays SuperAdmin-only on the
                    server. A refusal it still hits on submit (a race since the page loaded) is
                    reported in the error toast. */}
                {isVm && retryCheck?.allowed === true && (
                  <DropdownMenuItem
                    className="cursor-pointer"
                    disabled={
                      !isSuperAdmin() ||
                      retryProvisioning.isPending ||
                      isMenuBusy
                    }
                    title={
                      !isSuperAdmin()
                        ? "Only a SuperAdmin can retry provisioning"
                        : undefined
                    }
                    onClick={() => setConfirmRetryOpen(true)}
                  >
                    <RotateCcw className="mr-2 h-4 w-4" />
                    Retry provisioning…
                  </DropdownMenuItem>
                )}

                {/* Nothing in this group touches the infrastructure — every one of them only
                    corrects what WiCloud holds about the resource. Shown for a non-VM too, because
                    "Change status" is offered on every resource type: a disk's record drifts the
                    same way a VM's does, and the group label describes it exactly. */}
                {isSuperAdmin() && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuLabel className="text-xs font-medium text-muted-foreground">
                      WiCloud record
                    </DropdownMenuLabel>
                  </>
                )}
                {isVm &&
                  resource.status !== "Terminated" &&
                  resource.status !== "Terminating" && (
                    // The way back for a VM whose build never finished: the status sync only
                    // revisits rows already Running or Stopped, so an Error resource stays broken
                    // for ever while the customer is billed for it. Also how a drifted record is
                    // corrected since it replaced "Update mismatched configuration" (2026-10-26).
                    <PermissionGate roles="SuperAdmin">
                      <DropdownMenuItem
                        className="cursor-pointer"
                        disabled={
                          isMenuBusy || updateBlockedReason !== undefined
                        }
                        title={updateBlockedReason}
                        onClick={() =>
                          navigate(
                            `/admin/resources/${resource.id}/update-by-uuid`,
                          )
                        }
                      >
                        <LifeBuoy className="mr-2 h-4 w-4" />
                        Update resource by UUID
                      </DropdownMenuItem>
                    </PermissionGate>
                  )}
                {isVm && CONFIG_CHANGE_ENABLED && (
                  <PermissionGate roles="SuperAdmin">
                    <DropdownMenuItem
                      className="cursor-pointer"
                      onClick={() =>
                        navigate(`/admin/resources/${resource.id}/config`)
                      }
                    >
                      <SlidersHorizontal className="mr-2 h-4 w-4" />
                      Change configuration
                    </DropdownMenuItem>
                  </PermissionGate>
                )}
                {/* Update resource by UUID asks the provider what is really there; this one
                    asks nobody and simply writes the status down. It is the last resort of the
                    two, for a row the provider can no longer answer for — so it is offered on
                    every status and every resource type, and the dialog is where the risky
                    choices are explained. */}
                <PermissionGate roles="SuperAdmin">
                  <DropdownMenuItem
                    className="cursor-pointer"
                    disabled={isMenuBusy}
                    onClick={() => setIsChangingStatus(true)}
                  >
                    <RefreshCcwDot className="mr-2 h-4 w-4" />
                    Change status
                  </DropdownMenuItem>
                </PermissionGate>

                <PermissionGate roles="SuperAdmin">
                  {canEditExpiry && (
                    <DropdownMenuItem
                      className="cursor-pointer"
                      disabled={isMenuBusy}
                      onClick={() =>
                        navigate(`/admin/resources/${resource.id}/expiry`)
                      }
                    >
                      <CalendarClock className="mr-2 h-4 w-4" />
                      Change expiry date
                    </DropdownMenuItem>
                  )}

                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    className="cursor-pointer text-destructive focus:text-destructive"
                    disabled={
                      deleteResource.isPending || isNotDeletable || isMenuBusy
                    }
                    title={isNotDeletable ? deleteBlockedReason : undefined}
                    onClick={() => setConfirmDeleteOpen(true)}
                  >
                    <Trash2 className="mr-2 h-4 w-4" />
                    {deleteResource.isPending ? "Deleting..." : "Delete"}
                  </DropdownMenuItem>
                </PermissionGate>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        <div className="mt-5 grid gap-x-6 gap-y-4 border-t border-border/80 pt-5 text-sm sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
          {heroStats.map((col) => (
            <div key={col.label} className="flex flex-col gap-1">
              <span className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
                {col.label}
              </span>
              <div className="truncate font-medium text-foreground">
                {col.main}
              </div>
              <span className="truncate text-[11px] text-muted-foreground">
                {col.sub}
              </span>
            </div>
          ))}
        </div>
      </div>

      {error && (
        <Alert variant="default" className="border-warn/25 bg-warn-soft">
          <AlertTriangle className="h-4 w-4 text-warn" />
          <AlertTitle className="text-warn">
            Could not refresh this resource — showing the last loaded details
          </AlertTitle>
          <AlertDescription className="text-warn">
            <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
              <span>{error.message || "An unexpected error occurred."}</span>
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() => void refetch()}
                disabled={isFetching}
              >
                {isFetching && <Spinner className="mr-2" />}
                Retry
              </Button>
            </div>
          </AlertDescription>
        </Alert>
      )}

      {/* Why the resource is at Error, in the failing job's own words — the same line as the
          failed action's errorMessage on the Resource Actions tab, shown here so the operator does
          not have to open that tab to find it. `break-words` because a provider message can be one
          long unbroken token. */}
      {lastError && (
        <Alert
          variant="default"
          className="border-destructive/30 bg-destructive-soft"
        >
          <AlertTriangle className="h-4 w-4 text-destructive" />
          <AlertTitle className="text-destructive-ink">
            {lastError.at
              ? `Last error at ${formatVietnamDateTime(lastError.at)}`
              : "Last error"}
          </AlertTitle>
          <AlertDescription className="break-words font-mono text-xs text-foreground">
            {lastError.message}
          </AlertDescription>
        </Alert>
      )}

      {/* The backend puts a VM back to its previous status when n8n refuses its deletion for good,
          and records when. Hidden while a new deletion is running — that attempt has its own
          outcome to wait for. */}
      {resource.deletionFailedAt && resource.status !== "Terminating" && (
        <Alert
          variant="default"
          className="border-destructive/30 bg-destructive-soft"
        >
          <AlertTriangle className="h-4 w-4 text-destructive" />
          <AlertTitle className="text-destructive-ink">
            {`The last deletion at ${formatVietnamDateTime(resource.deletionFailedAt)} did not complete; the VM is unchanged.`}
          </AlertTitle>
          <AlertDescription className="text-foreground">
            Try again or check the infrastructure.
          </AlertDescription>
        </Alert>
      )}

      {/* The watch above stops polling once its cap passes (`REINSTALL_STATUS_WATCH_CAP_MS`), but a
          lost reinstall chain can still be running: `ReinstallRecoveryJob` only parks it at Error
          after its own StuckAfter (15 min) plus its scan cadence (10 min) plus another MaxWait
          (10 min) — up to ~35 minutes. Without this the header would read "Reinstalling" forever
          with nothing refreshing it. */}
      {isReinstallingLive && isReinstallWatchCapExpired && (
        <Alert variant="default" className="border-warn/25 bg-warn-soft">
          <AlertTriangle className="h-4 w-4 text-warn" />
          <AlertTitle className="text-warn">
            Still reinstalling — automatic refresh stopped
          </AlertTitle>
          <AlertDescription className="text-foreground">
            <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
              <span>
                This can take longer than usual to finish or fail. Refresh to
                check its latest status.
              </span>
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() => {
                  void refetch();
                  resumeReinstallWatch();
                }}
                disabled={isFetching}
              >
                {isFetching && <Spinner className="mr-2" />}
                Refresh
              </Button>
            </div>
          </AlertDescription>
        </Alert>
      )}

      {powerConfirmDialog}

      {/* Mounted only while open, so its "no machine exists" tick never survives to the next
          opening. */}
      {confirmRetryOpen && (
        <RetryProvisioningDialog
          open
          onOpenChange={setConfirmRetryOpen}
          resourceName={resource.name}
          status={resource.status}
          tenantName={resource.tenantName}
          createdAt={resource.createdAt}
          // Server-decided (2026-09-28): the batch check computes this now, so both consoles agree.
          // mayAlreadyExist={mayAlreadyExistOnProvider(resource)} — restore if the check is ever removed.
          mayAlreadyExist={retryCheck?.machineMayExist ?? false}
          recordedVmUuid={resource.providerResourceId}
          onUpdateByUuidInstead={() =>
            navigate(`/admin/resources/${resource.id}/update-by-uuid`)
          }
          isPending={retryProvisioning.isPending}
          onConfirm={({ recreateMissingVm }) => {
            // Closed either way: a refusal is explained in the toast, and retrying the same request
            // from an open dialog would only be refused again.
            retryProvisioning.mutate(
              { id: resource.id, recreateMissingVm },
              { onSettled: () => setConfirmRetryOpen(false) },
            );
          }}
        />
      )}

      {confirmReinstallOpen && (
        <ReinstallResourceDialog
          open
          onOpenChange={setConfirmReinstallOpen}
          resourceName={resource.name}
          currentOperatingSystem={resource.vm?.operatingSystem}
          vmCores={resource.vm?.vcpu}
          vmMemoryMb={resource.vm?.memoryMb}
          vmDiskGb={resource.vm?.diskSizeGb}
          resourceStatus={resource.status}
          providerResourceId={resource.providerResourceId}
          expiresAt={resource.expiresAt}
          isPending={reinstallResource.isPending}
          error={reinstallResource.error}
          onConfirm={(payload) => {
            // The row never leaves this page — it was already Live, now reading Reinstalling —
            // so the dialog just closes and the status watch above takes over.
            reinstallResource.mutate(
              { id: resource.id, payload },
              { onSuccess: () => setConfirmReinstallOpen(false) },
            );
          }}
        />
      )}

      <DeleteResourceDialog
        open={confirmDeleteOpen}
        onOpenChange={setConfirmDeleteOpen}
        resourceName={resource.name}
        resourceType={resource.type}
        resourceStatus={resource.status}
        providerResourceId={resource.providerResourceId}
        expiresAt={resource.expiresAt}
        tenantName={resource.tenantName}
        isPending={deleteResource.isPending}
        error={deleteResource.error}
        onShutDown={
          isSuperAdmin()
            ? () =>
                deleteDialogPowerAction.mutateAsync({
                  id: resource.id,
                  action: "shutdown",
                })
            : undefined
        }
        isPowerActionPending={
          deleteDialogPowerAction.isPending || isRunningPowerAction
        }
        onConfirm={(payload, recordOnly) => {
          const restorable = isRestorableDeletion(
            resource.type,
            resource.providerResourceId,
            !recordOnly,
          );
          deleteResource.mutate(
            { id: resource.id, payload, recordOnly, restorable },
            {
              // A restorable VM is still here, now Terminating: stay and let the re-read show it.
              // Any other deletion is finished and its detail dropped, so leave the page. A failure
              // leaves the dialog up so the operator can read the toast and retry.
              onSuccess: () => {
                if (restorable) {
                  setConfirmDeleteOpen(false);
                  return;
                }

                navigate("/admin/resources");
              },
            },
          );
        }}
      />

      {/* Tabs */}
      <Tabs
        value={activeTab}
        onValueChange={setActiveTab}
        className="space-y-5"
      >
        <div className="flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between">
          <TabsList className={tabListClass}>
            <TabsTrigger value="overview" className={tabTriggerClass}>
              <LayoutDashboard className="h-3.5 w-3.5 shrink-0" />
              Overview
            </TabsTrigger>
            {/* SuperAdmin only, like every other operator override on this page. The endpoint
                behind it checks the role itself — this keeps an operator who cannot use it from
                being shown a tab whose one button answers 403. */}
            {isVm && (
              <PermissionGate roles="SuperAdmin">
                <TabsTrigger value="access" className={tabTriggerClass}>
                  <KeyRound className="h-3.5 w-3.5 shrink-0" />
                  Access
                </TabsTrigger>
              </PermissionGate>
            )}
            <TabsTrigger value="metrics" className={tabTriggerClass}>
              <LineChart className="h-3.5 w-3.5 shrink-0" />
              Metrics
            </TabsTrigger>
            {isVm && (
              <TabsTrigger value="snapshots" className={tabTriggerClass}>
                <Camera className="h-3.5 w-3.5 shrink-0" />
                Snapshots
              </TabsTrigger>
            )}
            {isVm && (
              <TabsTrigger value="networking" className={tabTriggerClass}>
                <Network className="h-3.5 w-3.5 shrink-0" />
                Networking
              </TabsTrigger>
            )}
            {isVm && (
              <TabsTrigger value="storage" className={tabTriggerClass}>
                <HardDrive className="h-3.5 w-3.5 shrink-0" />
                Storage
              </TabsTrigger>
            )}
            <PermissionGate roles="SuperAdmin">
              <TabsTrigger value="n8n" className={tabTriggerClass}>
                <Webhook className="h-3.5 w-3.5 shrink-0" />
                n8n
              </TabsTrigger>
            </PermissionGate>
            <TabsTrigger value="activity" className={tabTriggerClass}>
              {/* Named and drawn as the sidebar's "Audit Logs" entry: the same rows, for one
                  resource. */}
              <ScrollText className="h-3.5 w-3.5 shrink-0" />
              Audit Logs
            </TabsTrigger>
            {/* The same two roles the endpoint behind it accepts — which is also every role this
                page admits, so today it only guards against the page's roles widening. */}
            <PermissionGate roles={["SuperAdmin", "SupportAdmin"]}>
              <TabsTrigger value="actions" className={tabTriggerClass}>
                <History className="h-3.5 w-3.5 shrink-0" />
                Resource Actions
              </TabsTrigger>
            </PermissionGate>
          </TabsList>

          {resource.orderNumber && (
            <div className="flex flex-col items-end gap-2 rounded-xl border border-border/80 bg-card px-3 py-2 text-right shadow-none sm:flex-row sm:items-center sm:justify-end">
              <span className="min-w-0 truncate text-sm font-semibold text-primary">
                {resource.orderNumber}
              </span>
              {resource.orderStatus && (
                <OrderStatusBadge status={resource.orderStatus} />
              )}
            </div>
          )}
        </div>

        <TabsContent value="overview">
          {isVm ? (
            <div className="grid gap-4 xl:grid-cols-[1.4fr_1fr] xl:items-start">
              <VmOverviewCharts
                metrics={overviewMetrics}
                isLoading={isLoadingOverviewMetrics}
                error={overviewMetricsError}
                memoryMb={resource.vm?.memoryMb}
                // With the query gated above, a VM that cannot report yet renders empty. Say
                // which of the two reasons it is, the way the Metrics tab does, rather than
                // leaving "No data yet" to look like a fault.
                emptyLabel={
                  metricsReady
                    ? undefined
                    : metricsPending
                      ? "Waiting for provisioning"
                      : "Metrics unavailable"
                }
              />
              <VmOverviewSideColumn resource={resource} />
            </div>
          ) : (
            <>
              <div className="grid gap-6 md:grid-cols-2">
                <Card className="border-border/80 shadow-none">
                  <CardHeader>
                    <CardTitle>Resource Information</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <div className="text-sm text-muted-foreground">
                          Type
                        </div>
                        <div className="font-medium">{resource.type}</div>
                      </div>
                      <div>
                        <div className="text-sm text-muted-foreground">
                          Region
                        </div>
                        <div className="font-medium">{resource.region}</div>
                      </div>
                      <div>
                        <div className="text-sm text-muted-foreground">
                          Tenant
                        </div>
                        <div className="font-medium">{resource.tenantName}</div>
                      </div>
                      <div>
                        <div className="text-sm text-muted-foreground">
                          Created
                        </div>
                        <div className="font-medium">
                          {formatDate(resource.createdAt)}
                        </div>
                      </div>
                      <div>
                        <div className="text-sm text-muted-foreground">
                          Last Updated
                        </div>
                        <div className="font-medium">
                          {formatDate(resource.updatedAt)}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-border/80 shadow-none">
                  <CardHeader>
                    <CardTitle>Specifications</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {Object.keys(specifications).length > 0 ? (
                      <div className="grid grid-cols-2 gap-3">
                        {Object.entries(specifications).map(([key, value]) => {
                          const displayKey = key
                            .replace(/([A-Z])/g, " $1")
                            .replace(/^./, (s) => s.toUpperCase())
                            .replace(/_/g, " ");
                          const displayValue =
                            typeof value === "object"
                              ? JSON.stringify(value)
                              : String(value);
                          return (
                            <div key={key}>
                              <div className="text-sm text-muted-foreground">
                                {displayKey}
                              </div>
                              <div className="font-medium text-sm">
                                {displayValue}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <p className="text-sm text-muted-foreground">
                        No specifications available
                      </p>
                    )}
                  </CardContent>
                </Card>
              </div>

              {tags.length > 0 && (
                <Card className="border-border/80 shadow-none">
                  <CardHeader>
                    <CardTitle>Tags</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex flex-wrap gap-2">
                      {tags.map((tag) => (
                        <Badge key={tag} variant="outline">
                          {tag}
                        </Badge>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </>
          )}
        </TabsContent>

        {isVm && (
          <PermissionGate roles="SuperAdmin">
            <TabsContent value="access">
              <VmPasswordResetPanel
                resourceId={resource.id}
                resourceName={resource.name}
                operatingSystem={resource.vm?.operatingSystem}
                status={resource.status}
              />
            </TabsContent>
          </PermissionGate>
        )}

        <TabsContent value="metrics">
          <div className="mb-4 flex justify-end gap-1">
            {METRICS_TIME_RANGES.map((range) => (
              <Button
                key={range.value}
                type="button"
                size="sm"
                variant={
                  metricsTimeRange === range.value ? "default" : "outline"
                }
                className="cursor-pointer"
                onClick={() => setMetricsTimeRange(range.value)}
                disabled={!metricsReady}
              >
                {range.label}
              </Button>
            ))}
          </div>
          {metricsReady ? (
            <ResourceMetricsChart
              metrics={metrics}
              isLoading={isLoadingMetrics}
              isFetching={isFetchingMetrics}
              isSwitchingRange={isSwitchingMetricsRange}
              error={metricsError}
              rangeLabel={selectedRange.label}
              onRetry={() => void refetchMetrics()}
            />
          ) : (
            <Card className="border-border/80 shadow-none">
              <CardContent className="flex flex-col items-center gap-1.5 py-16 text-center">
                <p className="text-sm font-medium text-foreground">
                  {metricsPending
                    ? "Waiting for provisioning to finish"
                    : "Metrics unavailable"}
                </p>
                <p className="max-w-sm text-sm text-muted-foreground">
                  {metricsPending
                    ? "This resource is still being provisioned. Metrics will appear once the VM is running."
                    : "No hypervisor-backed metrics exist for this resource."}
                </p>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {isVm && (
          <TabsContent value="snapshots">
            <Card className="border-border/80 shadow-none">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Camera className="h-4 w-4" />
                  Snapshots
                </CardTitle>
                <CardDescription>
                  Point-in-time snapshots of this VM
                </CardDescription>
              </CardHeader>
              <CardContent>
                {snapshotsError && snapshots && (
                  <TabQueryError
                    error={snapshotsError}
                    failedTitle="Failed to load snapshots"
                    onRetry={() => void refetchSnapshots()}
                    isRetrying={isFetchingSnapshots}
                    stale
                  />
                )}
                {isLoadingSnapshots ? (
                  // Rows in the shape of the table, like the Networking and Storage tabs. The
                  // snapshot list is the one read on this page that goes to the provider, so it is
                  // also the slowest to land — a line of text there read as a stall.
                  <div className="space-y-2">
                    <Skeleton className="h-10 w-full" />
                    <Skeleton className="h-10 w-full" />
                    <Skeleton className="h-10 w-full" />
                  </div>
                ) : snapshotsError && !snapshots ? (
                  <TabQueryError
                    error={snapshotsError}
                    failedTitle="Failed to load snapshots"
                    onRetry={() => void refetchSnapshots()}
                    isRetrying={isFetchingSnapshots}
                    notReadyAware
                  />
                ) : snapshots && snapshots.length > 0 ? (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Name</TableHead>
                        <TableHead>Description</TableHead>
                        <TableHead>Parent</TableHead>
                        <TableHead>State</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Created</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {snapshots.map((snapshot) => (
                        <TableRow key={snapshot.id}>
                          <TableCell className="font-medium">
                            {snapshot.name}
                          </TableCell>
                          <TableCell className="text-sm text-muted-foreground">
                            {snapshot.description || "—"}
                          </TableCell>
                          <TableCell className="text-sm text-muted-foreground">
                            {snapshot.parent || "—"}
                          </TableCell>
                          <TableCell>
                            {snapshot.vmstate === 1 ? (
                              <Badge variant="outline">RAM included</Badge>
                            ) : (
                              <span className="text-muted-foreground">
                                Disk only
                              </span>
                            )}
                          </TableCell>
                          <TableCell>
                            <Badge
                              variant={snapshotStatusVariant(snapshot.status)}
                            >
                              {snapshot.status}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-muted-foreground">
                            {formatDateTime(snapshot.createdAt)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                ) : (
                  <p className="py-6 text-center text-sm text-muted-foreground">
                    No snapshots
                  </p>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        )}

        {isVm && (
          <TabsContent value="networking">
            <div className="space-y-6">
              {/* Same shape as the client app's Network tab (ResourceNetworkTab.tsx): one row per
                  interface, IPv4 and region beside it, link state on the right. The client derives
                  that state from the VM's own status too — there is no per-NIC state on the wire. */}
              <Card className="border-border/80 shadow-none">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Network className="h-4 w-4" />
                    Network Interfaces
                  </CardTitle>
                  <CardDescription>
                    Public interface attached to this VM.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Interface</TableHead>
                        <TableHead>IPv4</TableHead>
                        <TableHead>Region</TableHead>
                        <TableHead className="text-right">Status</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      <TableRow>
                        <TableCell className="font-mono text-xs">
                          eth0 · public
                        </TableCell>
                        <TableCell className="font-mono text-xs">
                          {resource.vm?.publicIpAddress || "—"}
                        </TableCell>
                        <TableCell className="text-sm">
                          {resource.region || "—"}
                        </TableCell>
                        <TableCell className="text-right">
                          <Badge
                            variant="secondary"
                            className={cn(
                              "gap-1",
                              isInterfaceUp &&
                                "border-ok/30 bg-ok-soft text-ok-ink",
                            )}
                          >
                            {isInterfaceUp ? "Up" : "Down"}
                          </Badge>
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>

                  {/* Private addressing and VLAN membership are admin-only — the client tab never
                      sees them — so they sit under the table instead of reshaping the row. */}
                  <div className="mt-4 flex flex-wrap gap-x-10 gap-y-3 border-t border-border/80 pt-4">
                    <div>
                      <div className="text-xs text-muted-foreground">
                        Private IP
                      </div>
                      <div className="font-mono text-sm">
                        {resource.vm?.privateIpAddress || "—"}
                      </div>
                    </div>
                    <div>
                      <div className="text-xs text-muted-foreground">VLANs</div>
                      <div className="flex flex-wrap items-center gap-1 pt-0.5">
                        {resource.vm?.vlans && resource.vm.vlans.length > 0 ? (
                          resource.vm.vlans.map((vlan) => (
                            <Badge key={vlan} variant="outline">
                              {vlan}
                            </Badge>
                          ))
                        ) : (
                          <span className="font-mono text-sm">—</span>
                        )}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-border/80 shadow-none">
                <CardHeader className="flex flex-row items-center justify-between space-y-0">
                  <div>
                    <CardTitle>Attached Networks</CardTitle>
                    <CardDescription>
                      Network resources attached to this VM
                    </CardDescription>
                  </div>
                  {/* Placeholder, marked the way the client marks its own unlaunched actions:
                      the Admin API has no attach/detach endpoint, only GET {id}/attachments. */}
                  <Button
                    size="sm"
                    variant="outline"
                    className="cursor-pointer opacity-50"
                    title="Not available yet — the Admin API has no attach endpoint"
                    onClick={notWiredYet}
                  >
                    Attach Network
                  </Button>
                </CardHeader>
                <CardContent>
                  {attachmentsError && attachments && (
                    <TabQueryError
                      error={attachmentsError}
                      failedTitle="Failed to load networks"
                      onRetry={() => void refetchAttachments()}
                      isRetrying={isFetchingAttachments}
                      stale
                    />
                  )}
                  {isLoadingAttachments ? (
                    <div className="space-y-2">
                      <Skeleton className="h-16 w-full" />
                      <Skeleton className="h-16 w-full" />
                      <Skeleton className="h-16 w-full" />
                    </div>
                  ) : attachmentsError && !attachments ? (
                    <TabQueryError
                      error={attachmentsError}
                      failedTitle="Failed to load networks"
                      onRetry={() => void refetchAttachments()}
                      isRetrying={isFetchingAttachments}
                      notReadyAware
                    />
                  ) : attachments && attachments.networks.length > 0 ? (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Name</TableHead>
                          <TableHead>Bandwidth</TableHead>
                          <TableHead>Device ID</TableHead>
                          <TableHead>IP Address</TableHead>
                          <TableHead>VLAN</TableHead>
                          <TableHead>Attached At</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {attachments.networks.map((network) => (
                          <TableRow key={network.attachmentId}>
                            <TableCell className="font-medium">
                              {network.resourceName}
                            </TableCell>
                            <TableCell className="text-sm text-muted-foreground">
                              {network.bandwidth || "—"}
                            </TableCell>
                            <TableCell>
                              {network.deviceId ? (
                                <Badge variant="outline">
                                  {network.deviceId}
                                </Badge>
                              ) : (
                                <span className="text-muted-foreground">-</span>
                              )}
                            </TableCell>
                            <TableCell className="text-sm text-muted-foreground">
                              {network.ipAddress || "—"}
                            </TableCell>
                            <TableCell className="text-sm text-muted-foreground">
                              {network.vlanId ?? "—"}
                            </TableCell>
                            <TableCell className="text-muted-foreground">
                              {formatDateTime(network.attachedAt)}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  ) : (
                    <div className="rounded-lg border border-dashed p-8 text-center">
                      <Network className="mx-auto mb-4 h-12 w-12 text-muted-foreground" />
                      <h3 className="mb-2 text-lg font-semibold">
                        No networks attached
                      </h3>
                      <p className="text-sm text-muted-foreground">
                        Attach network interfaces to enable connectivity
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>

              {hasProviderVm(resource.providerResourceId) && (
                <ResourceFirewallPanel resourceId={resource.id} />
              )}
            </div>
          </TabsContent>
        )}

        {isVm && (
          <TabsContent value="storage">
            <div className="space-y-6">
              {/* Same shape as the client app's Storage tab (ResourceStorageTab.tsx): one block
                  per volume showing device, mount point, tier and provisioned IOPS, rather than a
                  flat table. The plan boot disk is synthesised above when `/attachments` does not
                  return it. */}
              <Card className="border-border/80 shadow-none">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <HardDrive className="h-4 w-4" />
                    Volumes ({storageVolumes.length})
                  </CardTitle>
                  <CardDescription>
                    Boot disk size comes from the VM's plan. Extra capacity is
                    attached as standalone disk resources.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {attachmentsError && attachments && (
                    <TabQueryError
                      error={attachmentsError}
                      failedTitle="Failed to load disks"
                      onRetry={() => void refetchAttachments()}
                      isRetrying={isFetchingAttachments}
                      stale
                    />
                  )}
                  {isLoadingAttachments ? (
                    <div className="space-y-3">
                      <Skeleton className="h-24 w-full rounded-lg" />
                      <Skeleton className="h-24 w-full rounded-lg" />
                    </div>
                  ) : attachmentsError && !attachments ? (
                    <TabQueryError
                      error={attachmentsError}
                      failedTitle="Failed to load disks"
                      onRetry={() => void refetchAttachments()}
                      isRetrying={isFetchingAttachments}
                      notReadyAware
                    />
                  ) : storageVolumes.length > 0 ? (
                    <div className="space-y-3">
                      {storageVolumes.map((volume) => (
                        <div
                          key={volume.key}
                          className="rounded-xl border border-border/80 bg-muted/10 p-3 sm:p-4"
                        >
                          <div className="flex flex-wrap items-center gap-2 gap-y-1 text-sm">
                            <HardDrive className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                            <span className="font-mono font-semibold">
                              /dev/{volume.deviceId || "scsi0"}
                            </span>
                            <span className="font-mono text-xs text-muted-foreground">
                              → {volume.mountLabel}
                            </span>
                            {volume.isBoot && (
                              <Badge
                                variant="secondary"
                                className="h-5 border-primary/30 bg-primary/10 text-[10px] text-primary"
                              >
                                BOOT
                              </Badge>
                            )}
                            <Badge
                              variant="outline"
                              className="h-5 text-[10px] font-normal"
                            >
                              {volume.typeLabel}
                            </Badge>
                            <span className="ml-auto font-mono text-[11px] tabular-nums text-muted-foreground">
                              {volume.iops != null
                                ? `${volume.iops.toLocaleString()} IOPS provisioned`
                                : "—"}
                            </span>
                          </div>
                          <div className="mt-2 flex flex-wrap gap-x-5 gap-y-1 font-mono text-[11px] text-muted-foreground">
                            <span>
                              <span className="text-foreground">
                                {volume.sizeGB != null
                                  ? `${volume.sizeGB} GB`
                                  : "—"}
                              </span>{" "}
                              provisioned
                            </span>
                            <span className="truncate">{volume.name}</span>
                            {/* Admin-only: the client tab never sees when a disk was attached. */}
                            {volume.attachedAt && (
                              <span className="ml-auto">
                                attached {formatDateTime(volume.attachedAt)}
                              </span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="rounded-lg border border-dashed px-4 py-8 text-center text-sm text-muted-foreground">
                      No disk volumes are listed for this VM yet.
                    </p>
                  )}
                </CardContent>
              </Card>

              <ResourceDiskIoCard
                metrics={metrics}
                isLoading={isLoadingMetrics}
                isFetching={isFetchingMetrics}
                error={metricsError}
                rangeLabel={selectedRange.label}
                onRetry={() => void refetchMetrics()}
                // The metrics query is disabled outside Running/Stopped/Suspended, so say why
                // instead of letting the card read as "no throughput". Same split as the
                // Metrics tab's placeholder.
                unavailableNote={
                  metricsReady
                    ? undefined
                    : metricsPending
                      ? "This resource is still being provisioned. Disk I/O will appear once the VM is running."
                      : "No hypervisor-backed metrics exist for this resource."
                }
              />
            </div>
          </TabsContent>
        )}

        <TabsContent value="activity">
          <div className="space-y-6">
            {/* "Activity" card hidden 2026-09-11: it listed the same resource_actions rows the
                "Resource Actions" tab now shows with paging and detail. To bring it back, uncomment
                this card together with the useResourceActions call and import, actionStatusVariant,
                formatDuration and the ActionStatus import, all commented out for it.
            <Card className="border-border/80 shadow-none">
              <CardHeader>
                <CardTitle>Activity</CardTitle>
                <CardDescription>
                  Recent actions performed on this resource
                </CardDescription>
              </CardHeader>
              <CardContent>
                {actionsError && actions && (
                  <TabQueryError
                    error={actionsError}
                    failedTitle="Failed to load activity"
                    onRetry={() => void refetchActions()}
                    isRetrying={isFetchingActions}
                    stale
                  />
                )}
                {isLoadingActions ? (
                  <div className="space-y-2">
                    <Skeleton className="h-16 w-full" />
                    <Skeleton className="h-16 w-full" />
                    <Skeleton className="h-16 w-full" />
                  </div>
                ) : actionsError && !actions ? (
                  <TabQueryError
                    error={actionsError}
                    failedTitle="Failed to load activity"
                    onRetry={() => void refetchActions()}
                    isRetrying={isFetchingActions}
                  />
                ) : actions && actions.length > 0 ? (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Action</TableHead>
                        <TableHead>Performed By</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Duration</TableHead>
                        <TableHead>Started</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {actions.map((action) => (
                        <TableRow key={action.id}>
                          <TableCell>
                            <div className="font-medium">{action.type}</div>
                            {action.description && (
                              <div className="text-sm text-muted-foreground">
                                {action.description}
                              </div>
                            )}
                            {action.errorMessage && (
                              <div className="mt-1 text-sm text-destructive-ink">
                                {action.errorMessage}
                              </div>
                            )}
                          </TableCell>
                          <TableCell>{action.userEmail}</TableCell>
                          <TableCell>
                            <Badge variant={actionStatusVariant(action.status)}>
                              {action.status}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-muted-foreground">
                            {formatDuration(action.durationMs)}
                          </TableCell>
                          <TableCell className="text-muted-foreground">
                            {action.startedAt
                              ? formatDateTime(action.startedAt)
                              : formatDateTime(action.createdAt)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                ) : (
                  <p className="py-6 text-center text-sm text-muted-foreground">
                    No activity recorded
                  </p>
                )}
              </CardContent>
            </Card>
            */}

            <Card className="border-border/80 shadow-none">
              <CardHeader>
                <CardTitle>Audit Logs</CardTitle>
                <CardDescription>
                  Audit trail of actions performed on this resource across its
                  lifecycle
                </CardDescription>
              </CardHeader>
              <CardContent>
                {activityLogError && activityLog && (
                  <TabQueryError
                    error={activityLogError}
                    failedTitle="Failed to load activity log"
                    onRetry={() => void refetchActivityLog()}
                    isRetrying={isFetchingActivityLog}
                    stale
                  />
                )}
                {isLoadingActivityLog ? (
                  <div className="space-y-3">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <Skeleton key={i} className="h-16 w-full" />
                    ))}
                  </div>
                ) : activityLogError && !activityLog ? (
                  <TabQueryError
                    error={activityLogError}
                    failedTitle="Failed to load activity log"
                    onRetry={() => void refetchActivityLog()}
                    isRetrying={isFetchingActivityLog}
                  />
                ) : activityLog && activityLog.data.length > 0 ? (
                  <div className="space-y-3">
                    {activityLog.data.map((entry) => (
                      <Card
                        key={entry.id}
                        className="border-border/80 shadow-none"
                      >
                        <CardContent className="p-4">
                          <div className="flex items-start justify-between gap-4">
                            <div className="flex min-w-0 items-start gap-3">
                              <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-panel-2">
                                <ActivityIcon className="h-4 w-4 text-muted-foreground" />
                              </div>
                              <div className="min-w-0">
                                <div className="flex flex-wrap items-center gap-2">
                                  <Badge
                                    variant={auditActionVariant(entry.action)}
                                  >
                                    {formatAuditAction(entry.action)}
                                  </Badge>
                                  {entry.category && (
                                    <span className="text-xs text-muted-foreground">
                                      {entry.category}
                                    </span>
                                  )}
                                </div>
                                {entry.description && (
                                  <p className="mt-1 truncate text-sm text-muted-foreground">
                                    {entry.description}
                                  </p>
                                )}
                              </div>
                            </div>
                            <div className="flex shrink-0 flex-col items-end gap-1 text-xs text-muted-foreground">
                              <div className="flex items-center gap-1">
                                <Clock className="h-3 w-3" />
                                {formatDateTime(entry.occurredAt)}
                              </div>
                              {entry.username && (
                                <div className="flex items-center gap-1">
                                  <User className="h-3 w-3" />
                                  {entry.username}
                                </div>
                              )}
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                ) : (
                  <div className="rounded-lg border border-dashed p-8 text-center">
                    <ActivityIcon className="mx-auto mb-4 h-12 w-12 text-muted-foreground" />
                    <h3 className="mb-2 text-lg font-semibold">
                      No activity recorded
                    </h3>
                    <p className="text-sm text-muted-foreground">
                      Actions performed on this resource will appear here
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <PermissionGate roles="SuperAdmin">
          <TabsContent value="n8n">
            {/* Probes and the write actions share one panel: the actions are the last entry of its
                endpoint menu, so an operator reaches them past the provider's own answers. */}
            <N8nProbePanel
              resourceId={id!}
              resourceName={resource?.name ?? "this VM"}
              vmUuid={
                hasProviderVm(resource?.providerResourceId)
                  ? resource?.providerResourceId?.trim()
                  : resource?.vm?.uuid?.trim()
              }
            />
          </TabsContent>
        </PermissionGate>

        <PermissionGate roles={["SuperAdmin", "SupportAdmin"]}>
          <TabsContent value="actions">
            {/* Keyed by the id: this page stays mounted across a move to another resource, and
                without a remount the tab would keep its page number and show the previous
                resource's rows as placeholder data until the new ones arrive. */}
            <ResourceActionsTab key={id} resourceId={id!} />
          </TabsContent>
        </PermissionGate>
      </Tabs>

      {/* The same dialog the resource table opens, driven the same way: it takes the target
          resource rather than an `open` flag, so a null closes it. */}
      <ChangeStatusDialog
        resource={statusTarget}
        onClose={() => setIsChangingStatus(false)}
        onSubmit={(status) =>
          updateStatus.mutate(
            { id: resource.id, status },
            // Left open on failure so the operator can read the toast and retry; the mutation
            // invalidates this resource's detail query, so the header re-reads the new status.
            { onSuccess: () => setIsChangingStatus(false) },
          )
        }
        isSubmitting={updateStatus.isPending}
      />

      {/* One entry point for both layers, driven the same way as the table's — the same VM, the
          same kind of change, whichever layer the operator picks inside it. Not closed on success
          when the machine path was taken: it switches to a result view, which is the only place a
          generated password is ever readable. */}
      <ChangeVmIpDialog
        resource={changeIpTarget}
        onClose={() => {
          setIsChangingIp(false);
          applyVmIp.reset();
        }}
        onSubmit={(payload) => {
          if (payload.target === "record") {
            updatePublicIp.mutate(
              {
                id: resource.id,
                ipAddress: payload.ipAddress,
                reason: payload.reason,
              },
              { onSuccess: () => setIsChangingIp(false) },
            );
            return;
          }

          applyVmIp.mutate({
            id: resource.id,
            payload: {
              ipAddress: payload.ipAddress,
              gateway: payload.gateway,
              resetPassword: payload.resetPassword,
              reason: payload.reason,
            },
          });
        }}
        isSubmitting={updatePublicIp.isPending || applyVmIp.isPending}
        result={applyVmIp.data}
      />

      <BusyOverlay
        open={busyLabel !== null}
        label={busyLabel ?? ""}
        hint={applyVmIp.isPending ? PROVIDER_WAIT_HINT : undefined}
      />
    </div>
  );
}
